import { hexToString, parseEventLogs, stringToHex, } from "viem";
import { protocolErrorAbi } from "./errors.js";
import { AssetKind, ListingAuthorizationKind, ListingPurchasability, ListingStatus, nativeAmount, } from "./types.js";
const uintInput = (name) => [{ name, type: "uint256" }];
const uintOutput = [{ name: "", type: "uint256" }];
export const MARKET_REASON = Object.freeze({
    PURCHASABLE: stringToHex("PURCHASABLE", { size: 32 }),
    PROTOCOL_PAUSED: stringToHex("PROTOCOL_PAUSED", { size: 32 }),
    PROJECT_NOT_ACTIVE: stringToHex("PROJECT_NOT_ACTIVE", { size: 32 }),
    OWNER_CHANGED: stringToHex("OWNER_CHANGED", { size: 32 }),
    AUTHORIZATION_CHANGED: stringToHex("AUTHORIZATION_CHANGED", { size: 32 }),
    ASSET_INVALID: stringToHex("ASSET_INVALID", { size: 32 }),
});
export const marketplaceAbi = [
    ...protocolErrorAbi,
    {
        type: "function",
        name: "getListing",
        stateMutability: "view",
        inputs: uintInput("listingId"),
        outputs: [
            {
                name: "listing",
                type: "tuple",
                components: [
                    { name: "listingId", type: "uint256" },
                    { name: "project", type: "address" },
                    { name: "assetContract", type: "address" },
                    { name: "tokenId", type: "uint256" },
                    { name: "assetKind", type: "uint8" },
                    { name: "seller", type: "address" },
                    { name: "priceWei", type: "uint256" },
                    { name: "ownershipEpoch", type: "uint64" },
                    { name: "authorizationKind", type: "uint8" },
                    { name: "authorizationEpoch", type: "uint64" },
                    { name: "marketEligibilityEpoch", type: "uint64" },
                    { name: "createdAt", type: "uint64" },
                    { name: "status", type: "uint8" },
                ],
            },
        ],
    },
    {
        type: "function",
        name: "purchasability",
        stateMutability: "view",
        inputs: uintInput("listingId"),
        outputs: [
            { name: "purchasable", type: "bool" },
            { name: "reason", type: "bytes32" },
        ],
    },
    ...["sellerCredit", "treasuryCredit"].map((name) => ({
        type: "function",
        name,
        stateMutability: "view",
        inputs: [{ name: name === "sellerCredit" ? "seller" : "treasuryAccount", type: "address" }],
        outputs: uintOutput,
    })),
    {
        type: "function",
        name: "list",
        stateMutability: "nonpayable",
        inputs: [
            { name: "assetContract", type: "address" },
            { name: "tokenId", type: "uint256" },
            { name: "priceWei", type: "uint256" },
        ],
        outputs: [{ name: "listingId", type: "uint256" }],
    },
    ...["cancel", "invalidate"].map((name) => ({
        type: "function",
        name,
        stateMutability: "nonpayable",
        inputs: uintInput("listingId"),
        outputs: [],
    })),
    {
        type: "function",
        name: "buy",
        stateMutability: "payable",
        inputs: [
            { name: "listingId", type: "uint256" },
            { name: "expectedPrice", type: "uint256" },
            { name: "deadline", type: "uint256" },
        ],
        outputs: [],
    },
    ...["withdrawSellerCredit", "withdrawTreasuryCredit"].map((name) => ({
        type: "function",
        name,
        stateMutability: "nonpayable",
        inputs: [{ name: "receiver", type: "address" }],
        outputs: [{ name: "amount", type: "uint256" }],
    })),
];
export const marketplaceNftAbi = [
    ...protocolErrorAbi,
    ...["ownerOf", "getApproved"].map((name) => ({
        type: "function",
        name,
        stateMutability: "view",
        inputs: uintInput("tokenId"),
        outputs: [{ name: "", type: "address" }],
    })),
    {
        type: "function",
        name: "isApprovedForAll",
        stateMutability: "view",
        inputs: [
            { name: "owner", type: "address" },
            { name: "operator", type: "address" },
        ],
        outputs: [{ name: "", type: "bool" }],
    },
    {
        type: "function",
        name: "approve",
        stateMutability: "nonpayable",
        inputs: [
            { name: "to", type: "address" },
            { name: "tokenId", type: "uint256" },
        ],
        outputs: [],
    },
    {
        type: "function",
        name: "setApprovalForAll",
        stateMutability: "nonpayable",
        inputs: [
            { name: "operator", type: "address" },
            { name: "approved", type: "bool" },
        ],
        outputs: [],
    },
];
export const marketplaceEventAbi = [
    {
        type: "event",
        name: "AssetListed",
        inputs: [
            { name: "listingId", type: "uint256", indexed: true },
            { name: "project", type: "address", indexed: true },
            { name: "seller", type: "address", indexed: true },
            { name: "assetContract", type: "address", indexed: false },
            { name: "tokenId", type: "uint256", indexed: false },
            { name: "assetKind", type: "uint8", indexed: false },
            { name: "priceWei", type: "uint256", indexed: false },
            { name: "ownershipEpoch", type: "uint64", indexed: false },
            { name: "authorizationKind", type: "uint8", indexed: false },
            { name: "authorizationEpoch", type: "uint64", indexed: false },
            { name: "marketEligibilityEpoch", type: "uint64", indexed: false },
            { name: "createdAt", type: "uint64", indexed: false },
        ],
    },
    ...["ListingCancelled", "ListingInvalidated"].map((name) => ({
        type: "event",
        name,
        inputs: name === "ListingCancelled"
            ? [
                { name: "listingId", type: "uint256", indexed: true },
                { name: "project", type: "address", indexed: true },
                { name: "seller", type: "address", indexed: true },
            ]
            : [
                { name: "listingId", type: "uint256", indexed: true },
                { name: "project", type: "address", indexed: true },
                { name: "reason", type: "bytes32", indexed: true },
            ],
    })),
    {
        type: "event",
        name: "AssetSold",
        inputs: [
            { name: "listingId", type: "uint256", indexed: true },
            { name: "project", type: "address", indexed: true },
            { name: "buyer", type: "address", indexed: true },
            { name: "seller", type: "address", indexed: false },
            { name: "assetContract", type: "address", indexed: false },
            { name: "tokenId", type: "uint256", indexed: false },
            { name: "assetKind", type: "uint8", indexed: false },
            { name: "priceWei", type: "uint256", indexed: false },
            { name: "sellerCreditWei", type: "uint256", indexed: false },
            { name: "treasuryCreditWei", type: "uint256", indexed: false },
        ],
    },
    {
        type: "event",
        name: "SellerCreditWithdrawn",
        inputs: [
            { name: "seller", type: "address", indexed: true },
            { name: "receiver", type: "address", indexed: true },
            { name: "amountWei", type: "uint256", indexed: false },
        ],
    },
    {
        type: "event",
        name: "TreasuryCreditWithdrawn",
        inputs: [
            { name: "source", type: "address", indexed: true },
            { name: "treasury", type: "address", indexed: true },
            { name: "receiver", type: "address", indexed: true },
            { name: "amountWei", type: "uint256", indexed: false },
        ],
    },
];
export function previewMarketFee(priceWei) {
    if (priceWei === 0n)
        throw new RangeError("Marketplace price must be greater than zero");
    const treasuryFeeWei = nativeAmount(priceWei / 100n);
    return {
        priceWei,
        treasuryFeeWei,
        sellerCreditWei: nativeAmount(priceWei - treasuryFeeWei),
    };
}
export function decodeMarketReason(reason) {
    const decoded = hexToString(reason, { size: 32 }).replaceAll("\0", "");
    return Object.hasOwn(MARKET_REASON, decoded) ? decoded : undefined;
}
export function marketReasonToEnum(reason) {
    return ListingPurchasability[reason];
}
export function listMarketAssetAction(assetContract, tokenId, priceWei) {
    previewMarketFee(priceWei);
    return { functionName: "list", args: [assetContract, tokenId, priceWei] };
}
export function buyMarketListingAction(listingId, expectedPrice, deadline) {
    if (deadline < 0n)
        throw new RangeError("Deadline must not be negative");
    return {
        functionName: "buy",
        args: [listingId, expectedPrice, deadline],
        value: expectedPrice,
    };
}
export function cancelMarketListingAction(listingId) {
    return { functionName: "cancel", args: [listingId] };
}
export function invalidateMarketListingAction(listingId) {
    return { functionName: "invalidate", args: [listingId] };
}
export function withdrawMarketCreditAction(kind, receiver) {
    return {
        functionName: kind === "seller" ? "withdrawSellerCredit" : "withdrawTreasuryCredit",
        args: [receiver],
    };
}
export function parseMarketplaceReceipt(receipt) {
    return parseEventLogs({ abi: marketplaceEventAbi, logs: receipt.logs, strict: false });
}
//# sourceMappingURL=market.js.map